import { MouseEvent } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { CompanySmall } from 'api';
import { dashboardCompanyOperationsValuationUrl } from 'dashboard409a/common/config/urls';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { ModelWithValuationData } from 'dashboard409a/services/models.types';
import { 
  findStandaloneCompanyForValuation, 
  formatMeasurementDate, 
  generateCompanyOperationsUrl 
} from 'hooks/useCompanyLookup';

/**
 * Handles row click navigation for valuation tables
 * @param row - The valuation row that was clicked
 * @param event - The mouse event
 * @param navigate - React Router navigate function
 * @param standaloneCompanies - Array of standalone companies for lookup
 */
export const handleValuationRowClick = (
  row: SimplifiedAttioValuation,
  event: MouseEvent<HTMLTableRowElement>,
  navigate: NavigateFunction,
  standaloneCompanies: CompanySmall[] = []
) => {
  // Prevent navigation if clicking on input elements
  if (event.target instanceof HTMLInputElement) {
    return;
  }

  // Find the matching standalone company for this Attio valuation
  const matchingCompany = findStandaloneCompanyForValuation(row, standaloneCompanies);
  
  if (matchingCompany) {
    // Generate the proper URL with company slug and ID
    const measurementDate = formatMeasurementDate(row.valuation_date);
    const url = generateCompanyOperationsUrl(matchingCompany, row.record_id, measurementDate);
    navigate(url);
  } else {
    // Fallback to the old behavior if no matching company is found
    const fallbackSlug = 'unknown-company';
    const fallbackUrl = dashboardCompanyOperationsValuationUrl(fallbackSlug, row.record_id);
    navigate(fallbackUrl);
  }
};

export const handleModelRowClick = (
  model: ModelWithValuationData,
  event: MouseEvent<HTMLTableRowElement>,
  navigate: NavigateFunction
) => {
  if (event.target instanceof HTMLInputElement || 
      event.target instanceof HTMLButtonElement ||
      event.target instanceof HTMLSelectElement ||
      (event.target as HTMLElement).closest('button') ||
      (event.target as HTMLElement).closest('input') ||
      (event.target as HTMLElement).closest('select')) {
    return;
  }

  if (model.company_slug && model.attio_valuation_id) {
    const url = dashboardCompanyOperationsValuationUrl(model.company_slug, model.attio_valuation_id);
    navigate(url);
  }
};

/**
 * Generates the fallback URL for unknown companies
 * @param valuationId - The valuation record ID
 * @returns The fallback URL string
 */
export const generateFallbackValuationUrl = (valuationId: string): string => {
  const fallbackSlug = 'unknown-company';
  return dashboardCompanyOperationsValuationUrl(fallbackSlug, valuationId);
};
